<script>
import { mapGetters } from 'vuex';
import AddAccountModal from '../dashboard/components/layout/sidebarComponents/AddAccountModal.vue';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import ReloadBanner from './components/app/ReloadBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import ConnectionLost from './components/app/ConnectionLost.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useColorScheme } from 'dashboard/composables/useColorScheme';
import { useVersionCheck } from 'dashboard/composables/useVersionCheck';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import PhoneAudioElements from 'components/PhoneAudioElements.vue';
import migrateDraftMessages from 'dashboard/helper/migrateDraftMessages';
import migrateNewConversationDraft from 'dashboard/helper/migrateNewConversationDraft';
import { setI18nLocale } from 'dashboard/i18n';

export default {
  name: 'App',

  components: {
    AddAccountModal,
    LoadingState,
    NetworkNotification,
    ReloadBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
    PhoneAudioElements,
    ConnectionLost,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    // Applies the color scheme once the `dark_mode` feature flag is resolved
    useColorScheme();
    const { hasNewVersion, startPolling, dismissNewVersion } =
      useVersionCheck();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      hasNewVersion,
      startPolling,
      dismissNewVersion,
    };
  },
  data() {
    return {
      showAddAccountModal: false,
      reconnectService: null,
      beforeUnloadHandler: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      globalConfig: 'globalConfig/get',
    }),
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentUser() {
      if (
        !this.hasAccounts &&
        this.globalConfig.createNewAccountFromDashboard
      ) {
        this.showAddAccountModal = true;
      }
    },
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    this.setLocale(window.chatwootConfig.selectedLocale);
    this.setupBeforeUnloadHandler();
    this.startPolling();
    migrateDraftMessages();
    migrateNewConversationDraft();
  },
  unmounted() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
    this.cleanupBeforeUnload();
  },
  methods: {
    setupBeforeUnloadHandler() {
      this.beforeUnloadHandler = () => {
        this.$store.dispatch('phone/disconnectIfOnline');
      };
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    },
    cleanupBeforeUnload() {
      if (this.beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        this.$store.dispatch('phone/disconnectIfOnline');
      }
    },
    setLocale(locale) {
      setI18nLocale(locale);
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale } = this.getAccount(this.currentAccountId);
      const { pubsub_token: pubsubToken, ui_settings: userUISettings } =
        this.currentUser || {};
      this.setLocale(userUISettings?.ui_locale || locale);
      vueActionCable.init(this.store, pubsubToken);
      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<template>
  <ConnectionLost v-if="authUIFlags.hasConnectionError" />
  <div
    v-else-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex-grow-0 w-full h-full min-h-0 app-wrapper"
    :class="{ 'app-rtl--wrapper': isRTL }"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <ReloadBanner :show="hasNewVersion" @dismiss="dismissNewVersion" />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <AddAccountModal :show="showAddAccountModal" :has-accounts="hasAccounts" />
    <WootSnackbarBox />
    <NetworkNotification />
  </div>
  <LoadingState v-else />
  <PhoneAudioElements />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

// The tooltip's hidden popper is display:none (leak guard, see the --hidden rule
// below), so it measures 0×0 and floating-ui's first on-show position is wrong;
// handleResize (dashboard.js) re-settles it a frame later. Because the popper
// comes from display:none, no CSS transition runs — that first (wrong, sometimes
// off-screen) frame is painted at full opacity and you can catch it jumping into
// place. A fade-in animation DOES run on appearance: we hold it fully transparent
// for its first half so the reposition lands before the tooltip is ever visible.
// Tooltips already open on a 200ms hover delay, so this reads as a subtle fade.
@keyframes cw-tooltip-appear {
  0%,
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.v-popper--theme-tooltip.v-popper__popper--shown {
  animation: cw-tooltip-appear 0.15s ease both;
}

// floating-vue keeps hidden poppers mounted (visibility: hidden) during the
// disposeTimeout window. A still-positioned, absolutely-placed popper at stale
// coordinates extends the document's scroll area and makes the viewport jump.
// display: none takes it out of layout entirely (we don't rely on the fade-out).
.v-popper__popper.v-popper__popper--hidden {
  display: none;
}

// A shown tooltip is appended at document level with pointer-events: auto. On tiny
// triggers (~20px status squares/bubbles) the popper — mispositioned over the trigger
// because the display:none-hidden box measures zero-size — sits under the cursor and
// steals the hover, causing mouseleave→hide→mouseenter→show flicker. Tooltips are
// non-interactive text, so make them click-through to break the loop; floating-ui then
// re-settles the popper above the trigger. Compound selector: floating-vue puts the
// theme class on the .v-popper__popper element itself, not an ancestor.
.v-popper__popper.v-popper--theme-tooltip {
  pointer-events: none;
}

.multiselect__input {
  margin-bottom: 0px !important;
}
</style>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
